import { useCallback, useEffect, useState, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Printer, Pencil, Trash2, Building2, KeyRound, ShieldOff, Copy, Check, Mail, Phone, ChevronDown, ChevronRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PartnerCompanyLinksPanel from "@/components/PartnerCompanyLinksPanel";
import CompanyGuaranteesPanel from "@/components/CompanyGuaranteesPanel";

const empty = { name: "", agency_share_percent: "", contact: "", contact_person: "", email: "", pec: "", phone: "", address: "", rui: "", notes: "", max_suspension_months: 12, claims_office_email: "", claims_office_pec: "", claims_office_phone: "", claims_office_notes: "" };

export default function Companies() {
  const { canWrite } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const autoNew = searchParams.get("new") === "1";
  const focusId = searchParams.get("focus");
  const returnTo = searchParams.get("return_to") || "";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [policies, setPolicies] = useState([]);
  // 🆕 09/07/2026 — Ricerca compagnie
  const [search, setSearch] = useState("");

  // 🆕 Se arriva ?new=1 dalla pagina Broker, apri automaticamente il modal
  useEffect(() => {
    if (autoNew) {
      setEditing(null);
      setForm(empty);
      setOpen(true);
    }
  }, [autoNew]);

  const goBackIfReturn = useCallback(() => {
    if (returnTo) {
      navigate(returnTo, { replace: true });
    }
  }, [returnTo, navigate]);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get("/companies"), api.get("/policies")]);
      setItems(c.data);
      setPolicies(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 🆕 Se arriva ?focus=<company_id> apri il dialog di modifica su quella compagnia
  useEffect(() => {
    if (!focusId || items.length === 0) return;
    const c = items.find((x) => x.id === focusId);
    if (c) {
      setEditing(c);
      setForm({ ...empty, ...c });
      setOpen(true);
    }
  }, [focusId, items]);

  const submit = async () => {
    try {
      // 🆕 09/07/2026 — Controllo duplicato client-side (case-insensitive, ignora spazi extra).
      // Blocca il salvataggio se esiste già un'altra compagnia con lo stesso nome.
      const rawName = (form.name || "").trim();
      if (!rawName) {
        toast.error("Il nome della compagnia è obbligatorio");
        return;
      }
      const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
      const isDup = items.some(
        (c) => norm(c.name) === norm(rawName) && (!editing || c.id !== editing.id),
      );
      if (isDup) {
        toast.error(`⚠️ Esiste già una compagnia con il nome "${rawName}"`);
        return;
      }
      const payload = {
        ...form,
        name: rawName,
        agency_share_percent: form.agency_share_percent === "" ? null : parseFloat(form.agency_share_percent),
        email: (form.email || "").trim() || null,
        pec: (form.pec || "").trim() || null,
        claims_office_email: (form.claims_office_email || "").trim() || null,
        claims_office_pec: (form.claims_office_pec || "").trim() || null,
        claims_office_phone: (form.claims_office_phone || "").trim() || null,
        claims_office_notes: (form.claims_office_notes || "").trim() || null,
        max_suspension_months: form.max_suspension_months === "" || form.max_suspension_months == null ? 12 : parseInt(form.max_suspension_months, 10),
      };
      if (editing) await api.put(`/companies/${editing.id}`, payload);
      else await api.post("/companies", payload);
      toast.success("Salvato");
      setOpen(false); setForm(empty); setEditing(null);
      load();
      goBackIfReturn();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare compagnia?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const [portalCreds, setPortalCreds] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // 🆕 Feb 2026 — Pannello espanso per compagnia: "partner" | "guarantees" | null
  const [expandedPanel, setExpandedPanel] = useState("partner");

  const togglePanel = (companyId, panel) => {
    if (expandedId === companyId && expandedPanel === panel) {
      setExpandedId(null);
    } else {
      setExpandedId(companyId);
      setExpandedPanel(panel);
    }
  };

  const enablePortal = async (c) => {
    if (!window.confirm(`Abilitare l'area compagnia per ${c.name}? Verrà generata una password una sola volta.`)) return;
    try {
      const { data } = await api.post(`/companies/${c.id}/enable-portal`);
      setPortalCreds({ ...data, name: c.name, recipientEmail: c.email, recipientPhone: c.phone, companyId: c.id });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendCredsEmail = async () => {
    if (!portalCreds?.companyId) return;
    try {
      const { data } = await api.post(`/companies/${portalCreds.companyId}/send-portal-credentials`, {
        portal_email: portalCreds.email, password: portalCreds.password,
        to_email: portalCreds.recipientEmail || portalCreds.email,
      });
      toast.success(`Credenziali inviate a ${data.recipient}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCredsWhatsApp = () => {
    if (!portalCreds?.recipientPhone) { toast.warning("Compagnia senza numero di telefono"); return; }
    let n = String(portalCreds.recipientPhone).replace(/[^\d+]/g, "");
    if (!n.startsWith("+")) {
      if (n.startsWith("00")) n = "+" + n.slice(2);
      else if (n.startsWith("3")) n = "+39" + n;
      else n = "+" + n;
    }
    const portalUrl = `${window.location.origin}/area-riservata`;
    const msg = encodeURIComponent(
      `Ciao ${portalCreds.name || ""}, abbiamo attivato la vostra area compagnia.\n\n🔐 Credenziali:\nEmail: ${portalCreds.email}\nPassword: ${portalCreds.password}\n\n🔗 Accedi: ${portalUrl}\n\nCambia la password al primo accesso.`,
    );
    window.open(`whatsapp://send?phone=${n.replace("+", "")}&text=${msg}`, "_blank", "noopener,noreferrer");
  };

  const disablePortal = async (c) => {
    if (!window.confirm(`Disattivare l'area compagnia per ${c.name}?`)) return;
    try { await api.post(`/companies/${c.id}/disable-portal`); toast.success("Area compagnia disattivata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Compute aggregated metrics per company (by name match)
  const enrichedItems = items.map((c) => {
    const own = policies.filter((p) => p.company_id === c.id || p.company === c.name);
    const prem = own.reduce((a, p) => a + (p.premium || 0), 0);
    const comm = own.reduce((a, p) => a + (p.commission_amount || 0), 0);
    return { ...c, _count: own.length, _premium: prem, _commission: comm };
  });
  // 🆕 Filtro ricerca (case-insensitive) su nome, email, PEC, RUI, telefono, referente
  const searchLower = search.trim().toLowerCase();
  const stats = searchLower
    ? enrichedItems.filter((c) => {
      const haystack = [
        c.name, c.email, c.pec, c.rui, c.phone, c.contact_person, c.contact, c.address, c.notes,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(searchLower);
    })
    : enrichedItems;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="companies-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600 bg-indigo-100 rounded-lg p-1.5" />Compagnie</h1>
          <p className="text-slate-500 mt-1">{items.length} compagnie · le provvigioni si configurano nei link Partner ↔ Compagnia</p>
          {/* 🆕 Feb 2026 — Warning se ci sono compagnie senza email (richiesta per email conferma offerte partner) */}
          {items.filter((c) => !c.email).length > 0 && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800" data-testid="companies-missing-email-warn">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span><strong>{items.filter((c) => !c.email).length}</strong> compagnie senza email: le email di conferma offerta non partiranno finché non la completi.</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* 🆕 Campo ricerca compagnie */}
          <div className="relative">
            <Input
              data-testid="company-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Cerca compagnia, email, RUI..."
              className="w-64 pr-8"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                title="Pulisci ricerca"
                data-testid="company-search-clear"
              >✕</button>
            )}
          </div>
          <Button variant="outline" data-testid="print-companies" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); goBackIfReturn(); } }}>
            <DialogTrigger asChild>
              {canWrite("companies") ? (
                <Button data-testid="add-company-btn" className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-2" /> Nuova compagnia</Button>
              ) : <span />}
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica compagnia" : "Nuova compagnia"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>% da dividere con la mia agenzia <span className="text-xs text-slate-400 font-normal">(facoltativo · fallback)</span></Label>
                  <Input
                    data-testid="company-agency-share"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.agency_share_percent ?? ""}
                    onChange={(e) => setForm({ ...form, agency_share_percent: e.target.value })}
                    placeholder="Es. 50"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">% della commissione che resta alla tua agenzia (il resto va al partner). Usata solo se non c&apos;è un broker assegnato sulla polizza.</p>
                </div>
                <div><Label>RUI</Label><Input data-testid="company-rui" value={form.rui || ""} onChange={(e) => setForm({ ...form, rui: e.target.value })} /></div>
                <div><Label>Persona di riferimento</Label><Input data-testid="company-contact-person" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input data-testid="company-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div>
                  <Label className="flex items-center gap-1">Email <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="company-email"
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="preventivi@..."
                    className={!form.email ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                  />
                  {!form.email && (
                    <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Obbligatoria per ricevere le conferme di offerta partner</p>
                  )}
                </div>
                <div><Label>PEC</Label><Input data-testid="company-pec" type="email" value={form.pec || ""} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
                <div>
                  <Label>⏸️ Termine max sospensione (mesi)</Label>
                  <Input
                    data-testid="company-max-suspension-months"
                    type="number"
                    min="1"
                    max="24"
                    value={form.max_suspension_months ?? 12}
                    onChange={(e) => setForm({ ...form, max_suspension_months: e.target.value })}
                    placeholder="12"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Standard: 12 mesi. Compagnie online (Prima, ConTe, Allianz Direct): 10. Veicoli storici: 11.</p>
                </div>
                <div className="col-span-2"><Label>Indirizzo</Label><Input data-testid="company-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="col-span-2"><Label>Contatto vario / Note brevi</Label><Input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

                {/* 🆕 Rubrica Ufficio Sinistri — precompila l'invio denuncia */}
                <div className="col-span-2 mt-3 border-t pt-3">
                  <div className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-1">
                    ⚠️ Ufficio Sinistri
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">
                    Questi contatti vengono precompilati automaticamente nel dialog &quot;Invia denuncia&quot; per le polizze di questa compagnia.
                  </p>
                </div>
                <div>
                  <Label>Email ufficio sinistri</Label>
                  <Input
                    data-testid="company-claims-email"
                    type="email"
                    value={form.claims_office_email || ""}
                    onChange={(e) => setForm({ ...form, claims_office_email: e.target.value })}
                    placeholder="sinistri@compagnia.it"
                  />
                </div>
                <div>
                  <Label>PEC ufficio sinistri</Label>
                  <Input
                    data-testid="company-claims-pec"
                    type="email"
                    value={form.claims_office_pec || ""}
                    onChange={(e) => setForm({ ...form, claims_office_pec: e.target.value })}
                    placeholder="sinistri@pec.compagnia.it"
                  />
                </div>
                <div>
                  <Label>Telefono ufficio sinistri</Label>
                  <Input
                    data-testid="company-claims-phone"
                    value={form.claims_office_phone || ""}
                    onChange={(e) => setForm({ ...form, claims_office_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Note ufficio sinistri</Label>
                  <Input
                    data-testid="company-claims-notes"
                    value={form.claims_office_notes || ""}
                    onChange={(e) => setForm({ ...form, claims_office_notes: e.target.value })}
                    placeholder="Es. inviare sempre in copia il broker"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setOpen(false); goBackIfReturn(); }}>Annulla</Button>
                <Button data-testid="save-company-btn" onClick={submit} disabled={!form.name} className="bg-[#0F172A] hover:bg-[#1E293B]">Salva</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="hidden print:block mb-6">
        <h1 className="font-display text-2xl font-semibold">Lista Compagnie — Assicura</h1>
        <p className="text-xs text-slate-500">{new Date().toLocaleDateString("it-IT")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-indigo-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-6 py-3">Compagnia</th>
              <th className="text-right px-6 py-3">Ripartizione</th>
              <th className="text-right px-6 py-3">Polizze</th>
              <th className="text-right px-6 py-3">Premi totali</th>
              <th className="text-right px-6 py-3">Provvigioni stimate</th>
              <th className="text-right px-6 py-3 print:hidden">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="companies-table">
            {stats.length === 0 && searchLower && (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-sm text-slate-500">
                  🔍 Nessuna compagnia trovata per <strong>&quot;{search}&quot;</strong>.
                  <button type="button" onClick={() => setSearch("")} className="ml-2 text-blue-600 hover:underline text-xs" data-testid="company-search-reset">Reimposta filtro</button>
                </td>
              </tr>
            )}
            {stats.map((c) => (
              <Fragment key={c.id}>
              <tr className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`toggle-company-${c.id}`}
                      onClick={() => togglePanel(c.id, "partner")}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${expandedId === c.id && expandedPanel === "partner" ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"}`}
                      title={expandedId === c.id && expandedPanel === "partner" ? "Chiudi partner collegati" : "Mostra partner collegati"}
                    >
                      {expandedId === c.id && expandedPanel === "partner" ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      Partner
                    </button>
                    {/* 🆕 Feb 2026 — Toggle Garanzie */}
                    <button
                      data-testid={`toggle-guarantees-${c.id}`}
                      onClick={() => togglePanel(c.id, "guarantees")}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${expandedId === c.id && expandedPanel === "guarantees" ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"}`}
                      title={expandedId === c.id && expandedPanel === "guarantees" ? "Chiudi garanzie" : "Mostra garanzie compagnia"}
                    >
                      {expandedId === c.id && expandedPanel === "guarantees" ? <ChevronDown className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      Garanzie
                    </button>
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{c.name}</span>
                    {/* 🆕 Badge Ufficio Sinistri configurato/mancante */}
                    {(c.claims_office_email || c.claims_office_pec) ? (
                      <span
                        data-testid={`claims-office-ok-${c.id}`}
                        title={`Ufficio sinistri: ${c.claims_office_email || c.claims_office_pec}${c.claims_office_phone ? " · ☎ " + c.claims_office_phone : ""}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800"
                      >
                        ⚠️ Sinistri OK
                      </span>
                    ) : (
                      <button
                        data-testid={`claims-office-missing-${c.id}`}
                        onClick={() => openEdit(c)}
                        title="Nessun contatto ufficio sinistri — clicca per aggiungerlo"
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      >
                        ⚠️ Sinistri da configurare
                      </button>
                    )}
                  </div>
                  {c.contact && <div className="text-xs text-slate-500 mt-0.5 ml-6">{c.contact}</div>}
                  <div className="flex flex-col gap-0.5 mt-1 ml-6">
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`company-email-${c.id}`}
                        className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                        title={`Invia email a ${c.email}`}
                      >
                        <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />{c.email}
                      </a>
                    )}
                    {c.phone && (
                      <a
                        href={`tel:${c.phone.replace(/\s+/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`company-phone-${c.id}`}
                        className="flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 hover:underline"
                        title={`Chiama ${c.phone}`}
                      >
                        <Phone className="h-3.5 w-3.5 text-amber-600 shrink-0" />{c.phone}
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-right tabular-nums">
                  {c.agency_share_percent != null ? (
                    <div className="text-xs text-blue-700">Quota agenzia fallback: {Number(c.agency_share_percent).toFixed(1)}%</div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right tabular-nums">{c._count}</td>
                <td className="px-6 py-4 text-right tabular-nums">{formatCurrency(c._premium)}</td>
                <td className="px-6 py-4 text-right tabular-nums font-medium text-emerald-700">{formatCurrency(c._commission)}</td>
                <td className="px-6 py-4 text-right print:hidden space-x-2">
                  {c.portal_enabled ? (
                    <button data-testid={`disable-company-portal-${c.id}`} onClick={() => disablePortal(c)} className="text-amber-600 hover:text-amber-800" title="Disabilita area compagnia">
                      <ShieldOff className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button data-testid={`enable-company-portal-${c.id}`} onClick={() => enablePortal(c)} className="text-emerald-600 hover:text-emerald-800" title="Abilita area compagnia">
                      <KeyRound className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button data-testid={`edit-company-${c.id}`} onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
              {expandedId === c.id && (
                <tr className="bg-slate-50/50 print:hidden">
                  <td colSpan={6} className="px-6 py-4">
                    {expandedPanel === "partner" && (
                      <PartnerCompanyLinksPanel
                        partnerType="compagnia"
                        partnerId={c.id}
                        partnerName={c.name}
                        canWrite={canWrite("companies")}
                        mode="by-company"
                      />
                    )}
                    {expandedPanel === "guarantees" && (
                      <CompanyGuaranteesPanel
                        companyId={c.id}
                        companyName={c.name}
                        canWrite={canWrite("companies")}
                      />
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!portalCreds} onOpenChange={(o) => !o && setPortalCreds(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700"><KeyRound className="h-5 w-5" /> Area compagnia abilitata</DialogTitle>
          </DialogHeader>
          {portalCreds && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Credenziali generate per <strong>{portalCreds.name}</strong>. Copiale ora: la password non sarà più visibile.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2 font-mono text-sm">
                <div><span className="text-slate-500 text-xs">Email:</span> {portalCreds.email}</div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">Password:</span> <span className="font-bold">{portalCreds.password}</span>
                  <button
                    data-testid="copy-company-creds"
                    onClick={async () => { await navigator.clipboard.writeText(`${portalCreds.email} / ${portalCreds.password}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="ml-auto text-xs text-blue-600 inline-flex items-center gap-1 hover:text-blue-800"
                  >
                    {copied ? <><Check className="h-3 w-3" /> Copiato</> : <><Copy className="h-3 w-3" /> Copia</>}
                  </button>
                </div>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">⚠️ Salva queste credenziali ora. Per rigenerare la password, disabilita e riabilita l&apos;area.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button data-testid="company-send-email" variant="outline" onClick={sendCredsEmail} disabled={!portalCreds.recipientEmail} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Mail className="h-4 w-4 mr-1.5" /> Invia via email
                </Button>
                <Button data-testid="company-send-wa" variant="outline" onClick={openCredsWhatsApp} disabled={!portalCreds.recipientPhone} className="border-green-300 text-green-700 hover:bg-green-50">
                  💬 Apri WhatsApp
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPortalCreds(null)} className="bg-[#0F172A] hover:bg-[#1E293B]">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
